import React from "react";

const createSession = () => ({
  id: `cardio-${Date.now()}-${Math.random()
    .toString(16)
    .slice(2)}`,
  type: "treadmill",
  sessionsPerWeek: "",
  duration: "",
  speed: "",
  incline: "",
  level: "",
  resistance: "",
  watts: "",
  pace: "",
  intensityNote: "",
  heartRate: "",
  isOpen: true,
});

const fieldsByType = {
  treadmill: [
    { label: "Speed (km/h)", key: "speed", type: "number" },
    { label: "Incline (%)", key: "incline", type: "number" },
  ],
  stairmaster: [
    { label: "Level / speed", key: "level", type: "number" },
  ],
  elliptical: [
    { label: "Resistance", key: "resistance", type: "number" },
    { label: "Incline (%)", key: "incline", type: "number" },
  ],
  bike: [
    { label: "Resistance", key: "resistance", type: "number" },
    { label: "Watts (optional)", key: "watts", type: "number" },
  ],
  rowing: [
    { label: "Pace (500m split)", key: "pace", type: "text" },
    { label: "Resistance", key: "resistance", type: "number" },
  ],
  other: [
    {
      label: "Intensity notes",
      key: "intensityNote",
      type: "text",
    },
  ],
};

export default function CardioList({ sessions, setSessions }) {
  const hasSessions = sessions.length > 0;

  const addSession = () =>
    setSessions((prev) => [...prev, createSession()]);

  const removeSession = (id) =>
    setSessions((prev) => prev.filter((item) => item.id !== id));

  const updateSession = (id, updates) =>
    setSessions((prev) =>
      prev.map((item) =>
        item.id === id ? { ...item, ...updates } : item
      )
    );

  const toggleOpen = (id) =>
    setSessions((prev) =>
      prev.map((item) =>
        item.id === id
          ? { ...item, isOpen: !item.isOpen }
          : item
      )
    );

  return (
    <div className="glass-card p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="text-lg font-display text-[var(--ink)]">
            Weekly cardio sessions
          </h3>
          <p className="text-sm text-[var(--muted)]">
            Log the sessions you typically do in a week. Workout-based
            mode converts them into calories.
          </p>
        </div>
      </div>

      <div className="mt-3 flex flex-wrap gap-2 text-xs">
        <span
          className={`rounded-full px-3 py-1 ${
            hasSessions
              ? "bg-white/80 text-[var(--ink)]"
              : "bg-white/60 text-[var(--muted)]"
          }`}
        >
          {hasSessions
            ? "Cardio detail boost active"
            : "Add 1+ session for +10% confidence"}
        </span>
      </div>

      <div className="mt-4 space-y-3">
        {sessions.map((session, index) => (
          <div
            key={session.id}
            className="rounded-2xl border border-white/70 bg-white/70"
          >
            <div className="session-row flex items-center justify-between gap-3 px-4 py-3">
              <button
                type="button"
                onClick={() => toggleOpen(session.id)}
                className="flex items-center gap-3 text-left"
              >
                <span className="text-sm font-semibold text-[var(--ink)]">
                  Cardio {index + 1}
                </span>
                <span className="text-xs text-[var(--muted)] capitalize">
                  {session.type}
                </span>
              </button>
              <button
                type="button"
                onClick={() => removeSession(session.id)}
                className="session-row__remove text-xs text-[var(--accent)] hover:underline"
              >
                Remove
              </button>
            </div>

            <div
              className={`grid gap-4 overflow-hidden px-4 transition-all duration-500 ${
                session.isOpen
                  ? "max-h-[520px] pb-4 opacity-100"
                  : "max-h-0 opacity-0"
              }`}
            >
              <div className="grid gap-3 md:grid-cols-2">
                <label className="text-xs text-[var(--muted)]">
                  Machine type
                  <select
                    className="select-field mt-1"
                    value={session.type}
                    onChange={(event) =>
                      updateSession(session.id, {
                        type: event.target.value,
                      })
                    }
                  >
                    <option value="treadmill">Treadmill</option>
                    <option value="stairmaster">StairMaster</option>
                    <option value="elliptical">Elliptical</option>
                    <option value="bike">Bike</option>
                    <option value="rowing">Rowing</option>
                    <option value="other">Other</option>
                  </select>
                </label>
                <label className="text-xs text-[var(--muted)]">
                  Duration per session (minutes)
                  <input
                    type="number"
                    min="0"
                    className="input-field mt-1"
                    value={session.duration}
                    onChange={(event) =>
                      updateSession(session.id, {
                        duration: event.target.value,
                      })
                    }
                  />
                </label>
              </div>

              <label className="text-xs text-[var(--muted)]">
                Sessions per week
                <input
                  type="number"
                  min="0"
                  max="7"
                  className="input-field mt-1"
                  value={session.sessionsPerWeek}
                  onChange={(event) =>
                    updateSession(session.id, {
                      sessionsPerWeek: event.target.value,
                    })
                  }
                />
              </label>

              <div className="grid gap-3 md:grid-cols-2">
                {(fieldsByType[session.type] || []).map(
                  (field) => (
                    <label
                      key={field.key}
                      className="text-xs text-[var(--muted)]"
                    >
                      {field.label}
                      <input
                        type={field.type}
                        min={field.type === "number" ? "0" : undefined}
                        className="input-field mt-1"
                        value={session[field.key]}
                        onChange={(event) =>
                          updateSession(session.id, {
                            [field.key]: event.target.value,
                          })
                        }
                      />
                    </label>
                  )
                )}
              </div>

              <label className="text-xs text-[var(--muted)]">
                Average heart rate (bpm, optional)
                <input
                  type="number"
                  min="0"
                  className="input-field mt-1"
                  value={session.heartRate}
                  onChange={(event) =>
                    updateSession(session.id, {
                      heartRate: event.target.value,
                    })
                  }
                />
              </label>
            </div>
          </div>
        ))}
      </div>

      <button
        type="button"
        onClick={addSession}
        className="mt-4 inline-flex items-center justify-center rounded-full border border-white/70 bg-white/80 px-4 py-2 text-sm font-semibold text-[var(--ink)] transition hover:-translate-y-0.5 hover:shadow-lg"
      >
        Add weekly cardio session
      </button>
    </div>
  );
}
